import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Box,
  Flex,
  Text,
  Input,
  IconButton,
  VStack,
  HStack,
  Spinner,
  Button,
  Tag,
  TagLabel,
  useColorModeValue,
  Icon,
  keyframes,
} from '@chakra-ui/react';
import { LuSend, LuPlus, LuArrowLeft, LuMessageCircle, LuWrench, LuSparkles, LuTrendingUp, LuSearch, LuTarget, LuCornerDownRight } from 'react-icons/lu';
import ReactMarkdown from 'react-markdown';
import { sendChatMessage, getChatStatus, listConversations, getConversation } from '../services/chatService';
import { logger } from '../utils/logger';

// Animation keyframes
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
`;

// Friendly display names for agent tools
const TOOL_LABELS = {
  get_businesses: 'Queried businesses',
  get_monthly_trends: 'Checked monthly trends',
  get_revenue_estimates: 'Fetched revenue estimates',
  get_market_overview: 'Loaded market overview',
  get_competitive_intel: 'Looked up competitive intel',
  save_business_insight: 'Saved insight',
  save_report: 'Saved report',
  search_reviews: 'Searched reviews',
};

// Follow-up suggestions based on tools used or conversation context
const FOLLOW_UP_SUGGESTIONS = {
  get_businesses: [
    'Which of these businesses has the most reviews?',
    'Show me the lowest rated businesses and why',
    'What common themes appear in their reviews?',
    'Compare the top 3 businesses side by side',
  ],
  get_monthly_trends: [
    'Which business improved the most recently?',
    'Are there any seasonal patterns in the data?',
    'Who is losing momentum and why?',
    'Predict which businesses will grow next month',
  ],
  get_revenue_estimates: [
    'Which business has the best review-to-revenue ratio?',
    'Are there underperformers with hidden potential?',
    'How does rating correlate with revenue here?',
    'Who is the most efficient competitor?',
  ],
  get_market_overview: [
    'What gaps exist that no business is filling?',
    'Which positive themes should I focus on?',
    'What are customers complaining about most?',
    'How does this market compare to industry standards?',
  ],
  get_competitive_intel: [
    'How can I outrank this competitor online?',
    'What keywords are they missing?',
    'Compare their web presence to the market leader',
    'What advertising strategies are working for them?',
  ],
  search_reviews: [
    'Find the opposite sentiment in reviews',
    'Which businesses handle this issue best?',
    'Are there patterns in when these reviews were posted?',
    'What do 5-star reviews say about this topic?',
  ],
  default: [
    'Show me the top performers in this market',
    'What are customers complaining about most?',
    'Find businesses with great reviews but few customers',
    'Generate a competitive analysis report',
  ],
};

// Get contextual follow-up suggestions based on tools called
function getFollowUpSuggestions(toolsCalled = []) {
  const suggestions = new Set();
  
  // Add suggestions based on tools used
  toolsCalled.forEach(tc => {
    const toolSuggestions = FOLLOW_UP_SUGGESTIONS[tc.tool];
    if (toolSuggestions) {
      // Pick 1-2 random suggestions from each tool
      const shuffled = [...toolSuggestions].sort(() => 0.5 - Math.random());
      shuffled.slice(0, 2).forEach(s => suggestions.add(s));
    }
  });
  
  // If we don't have enough, add from defaults
  if (suggestions.size < 2) {
    const defaults = [...FOLLOW_UP_SUGGESTIONS.default].sort(() => 0.5 - Math.random());
    defaults.forEach(s => {
      if (suggestions.size < 2) suggestions.add(s);
    });
  }
  
  // Return exactly 2 suggestions
  return Array.from(suggestions).slice(0, 2);
}

// Quick prompts for drawer (shorter versions)
const QUICK_PROMPTS = [
  {
    icon: LuSearch,
    text: 'Find negative reviews about pricing',
    gradient: 'linear(to-br, purple.400, pink.400)',
  },
  {
    icon: LuTrendingUp,
    text: 'Show me trending businesses this month',
    gradient: 'linear(to-br, blue.400, cyan.400)',
  },
  {
    icon: LuTarget,
    text: 'Which competitor has the best reviews?',
    gradient: 'linear(to-br, green.400, teal.400)',
  },
  {
    icon: LuSparkles,
    text: 'Generate a market overview report',
    gradient: 'linear(to-br, orange.400, yellow.400)',
  },
];

function QuickPromptButton({ icon, text, gradient, onClick }) {
  return (
    <Box
      as="button"
      onClick={onClick}
      w="100%"
      p={3}
      borderRadius="xl"
      bg="white"
      border="1px solid"
      borderColor="gray.100"
      textAlign="left"
      transition="all 0.2s"
      _hover={{
        transform: 'translateY(-1px)',
        shadow: 'md',
        borderColor: 'gray.200',
      }}
      _active={{
        transform: 'translateY(0)',
      }}
    >
      <HStack spacing={3}>
        <Flex
          w="32px"
          h="32px"
          borderRadius="lg"
          bgGradient={gradient}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={icon} color="white" boxSize={4} />
        </Flex>
        <Text fontSize="sm" color="gray.700" fontWeight="500" noOfLines={1}>
          {text}
        </Text>
      </HStack>
    </Box>
  );
}

function FollowUpSuggestion({ text, onClick }) {
  return (
    <Box
      as="button"
      onClick={onClick}
      w="100%"
      p={2.5}
      borderRadius="lg"
      bg="white"
      border="1px solid"
      borderColor="blue.100"
      textAlign="left"
      transition="all 0.2s"
      _hover={{
        bg: 'blue.50',
        borderColor: 'blue.200',
        transform: 'translateX(2px)',
      }}
      _active={{
        transform: 'translateX(0)',
      }}
    >
      <HStack spacing={2} align="flex-start">
        <Flex
          w="18px"
          h="18px"
          borderRadius="md"
          bg="blue.50"
          align="center"
          justify="center"
          flexShrink={0}
          mt={0.5}
        >
          <Icon as={LuCornerDownRight} color="blue.500" boxSize={3} />
        </Flex>
        <Text fontSize="xs" color="gray.700" fontWeight="500" lineHeight="1.4">
          {text}
        </Text>
      </HStack>
    </Box>
  );
}

function AssistantMessage({ content, toolsCalled, onFollowUpClick, isLastMessage }) {
  const toolBg = useColorModeValue('blue.50', 'gray.600');
  const toolColor = useColorModeValue('blue.600', 'blue.200');
  const toolBorder = useColorModeValue('blue.100', 'gray.500');

  return (
    <Box>
      {/* Inline tool call tags */}
      {toolsCalled && toolsCalled.length > 0 && (
        <HStack spacing={1.5} mb={3} flexWrap="wrap">
          <Flex
            w="16px"
            h="16px"
            borderRadius="md"
            bg="blue.50"
            align="center"
            justify="center"
            flexShrink={0}
          >
            <Box as={LuWrench} size="9px" color="blue.500" />
          </Flex>
          {toolsCalled.map((tc, i) => (
            <Tag
              key={i}
              size="sm"
              bg={toolBg}
              borderRadius="full"
              border="1px solid"
              borderColor={toolBorder}
              px={2}
              py={0.5}
            >
              <TagLabel fontSize="xs" color={toolColor} fontWeight="500">
                {TOOL_LABELS[tc.tool] || tc.tool}
              </TagLabel>
            </Tag>
          ))}
        </HStack>
      )}
      {/* Markdown-rendered content */}
      <Box
        fontSize="sm"
        lineHeight="1.6"
        sx={{
          '& p': { mb: 2 },
          '& p:last-child': { mb: 0 },
          '& ul, & ol': { pl: 4, mb: 2 },
          '& li': { mb: 1 },
          '& h1': { fontSize: 'lg', fontWeight: 'bold', mb: 2, mt: 3 },
          '& h2': { fontSize: 'md', fontWeight: 'bold', mb: 2, mt: 3 },
          '& h3': { fontSize: 'sm', fontWeight: 'bold', mb: 1, mt: 2 },
          '& strong': { fontWeight: '600', color: 'gray.800' },
          '& code': {
            bg: 'gray.100',
            px: 1.5,
            py: 0.5,
            borderRadius: 'md',
            fontSize: 'xs',
          },
          '& pre': {
            bg: 'gray.50',
            p: 3,
            borderRadius: 'lg',
            overflowX: 'auto',
            mb: 2,
            border: '1px solid',
            borderColor: 'gray.200',
          },
          '& table': { width: '100%', mb: 2 },
          '& th, & td': {
            border: '1px solid',
            borderColor: 'gray.200',
            px: 2,
            py: 1,
            fontSize: 'xs',
          },
          '& th': { fontWeight: '600', bg: 'gray.50' },
          '& a': { color: 'blue.500', textDecoration: 'underline', fontWeight: '500' },
        }}
      >
        <ReactMarkdown>{content}</ReactMarkdown>
      </Box>
      
      {/* Follow-up suggestions - only show on the last assistant message */}
      {isLastMessage && (
        <VStack spacing={2} mt={4} align="stretch">
          <Text fontSize="xs" color="gray.500" fontWeight="500">
            Continue exploring:
          </Text>
          {getFollowUpSuggestions(toolsCalled).map((suggestion, i) => (
            <FollowUpSuggestion
              key={i}
              text={suggestion}
              onClick={() => onFollowUpClick(suggestion)}
            />
          ))}
        </VStack>
      )}
    </Box>
  );
}

function ChatDrawer({ isOpen, onClose, initialMessage = '' }) {
  const [view, setView] = useState('chat'); // 'list' or 'chat'
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [streamingTools, setStreamingTools] = useState([]);
  const [hasProcessedInitialMessage, setHasProcessedInitialMessage] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pollingRef = useRef(null);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const listHoverBg = useColorModeValue('gray.50', 'gray.700');

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingTools, scrollToBottom]);

  // Focus input when drawer opens or switches to chat view
  useEffect(() => {
    if (isOpen && view === 'chat') {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen, view]);

  // Handle initial message when drawer opens
  useEffect(() => {
    if (isOpen && initialMessage && !hasProcessedInitialMessage && !isLoading) {
      setHasProcessedInitialMessage(true);
      // Small delay to ensure drawer is fully rendered
      setTimeout(() => {
        handleSend(initialMessage);
      }, 150);
    }
  }, [isOpen, initialMessage, hasProcessedInitialMessage, isLoading]);

  // Reset hasProcessedInitialMessage when drawer closes
  useEffect(() => {
    if (!isOpen) {
      setHasProcessedInitialMessage(false);
    }
  }, [isOpen]);

  // Clean up polling on unmount or drawer close
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, []);

  const loadConversations = useCallback(async () => {
    setIsLoadingList(true);
    try {
      const data = await listConversations();
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (error) {
      logger.error('Failed to load conversations:', error);
    } finally {
      setIsLoadingList(false);
    }
  }, []);

  const openConversation = useCallback(async (convId) => {
    setIsLoading(true);
    try {
      const data = await getConversation(convId);
      if (data.success) {
        setMessages(data.conversation.messages || []);
        setConversationId(convId);
        setView('chat');
      }
    } catch (error) {
      logger.error('Failed to load conversation:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const startNewChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setView('chat');
    setInputValue('');
    setStreamingTools([]);
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const handleSend = useCallback(async (customMessage = null) => {
    const trimmed = (customMessage || inputValue).trim();
    if (!trimmed || isLoading) return;

    // Optimistically add user message
    const userMsg = { role: 'user', content: trimmed, timestamp: new Date().toISOString() };
    setMessages((prev) => [...prev, userMsg]);
    setInputValue('');
    setIsLoading(true);
    setStreamingTools([]);

    try {
      const data = await sendChatMessage(trimmed, conversationId);

      if (!data.success) {
        throw new Error('Failed to send message');
      }

      const activeConvId = data.conversation_id;
      if (!conversationId) {
        setConversationId(activeConvId);
      }

      // Poll for agent progress
      pollingRef.current = setInterval(async () => {
        try {
          const status = await getChatStatus(activeConvId);

          // Update tool tags in real time
          if (status.tools_called && status.tools_called.length > 0) {
            setStreamingTools(status.tools_called);
          }

          if (status.status === 'done') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;

            const assistantMsg = {
              role: 'assistant',
              content: status.reply,
              timestamp: new Date().toISOString(),
              tools_called: status.tools_called || [],
            };
            setMessages((prev) => [...prev, assistantMsg]);
            setStreamingTools([]);
            setIsLoading(false);
          } else if (status.status === 'error') {
            clearInterval(pollingRef.current);
            pollingRef.current = null;

            const errorMsg = {
              role: 'assistant',
              content: status.error || 'Sorry, something went wrong. Please try again.',
              timestamp: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, errorMsg]);
            setStreamingTools([]);
            setIsLoading(false);
          }
        } catch (pollError) {
          logger.error('Polling error:', pollError);
          clearInterval(pollingRef.current);
          pollingRef.current = null;

          const errorMsg = {
            role: 'assistant',
            content: 'Sorry, something went wrong. Please try again.',
            timestamp: new Date().toISOString(),
          };
          setMessages((prev) => [...prev, errorMsg]);
          setStreamingTools([]);
          setIsLoading(false);
        }
      }, 1000);
    } catch (error) {
      logger.error('Failed to send message:', error);
      const errorMsg = {
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
      setStreamingTools([]);
      setIsLoading(false);
    }
  }, [inputValue, isLoading, conversationId]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const showConversationList = useCallback(() => {
    setView('list');
    loadConversations();
  }, [loadConversations]);

  const handleQuickPrompt = useCallback((prompt) => {
    handleSend(prompt);
  }, [handleSend]);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay bg="blackAlpha.300" backdropFilter="blur(4px)" />
      <DrawerContent borderLeftRadius="2xl" overflow="hidden">
        {/* Header */}
        <DrawerHeader 
          borderBottomWidth="1px" 
          borderColor={borderColor} 
          py={4}
          bg="white"
          pr={12}
        >
          <Flex align="center" justify="space-between">
            <HStack spacing={3}>
              {view === 'list' ? (
                <Text fontWeight="600" fontSize="lg" color="gray.800">
                  Conversations
                </Text>
              ) : (
                <>
                  {conversationId && (
                    <IconButton
                      icon={<LuArrowLeft />}
                      variant="ghost"
                      size="sm"
                      onClick={showConversationList}
                      aria-label="Back to conversations"
                      borderRadius="lg"
                    />
                  )}
                  <HStack spacing={2}>
                    <Flex
                      w="28px"
                      h="28px"
                      borderRadius="lg"
                      bgGradient="linear(to-br, blue.400, purple.500)"
                      align="center"
                      justify="center"
                      animation={`${float} 3s ease-in-out infinite`}
                    >
                      <Icon as={LuSparkles} color="white" boxSize={3.5} />
                    </Flex>
                    <Text fontWeight="600" fontSize="lg" color="gray.800">
                      AI Assistant
                    </Text>
                  </HStack>
                </>
              )}
            </HStack>
            <HStack spacing={1}>
              {view === 'chat' && (
                <IconButton
                  icon={<LuMessageCircle />}
                  variant="ghost"
                  size="sm"
                  onClick={showConversationList}
                  aria-label="View conversations"
                  title="Conversation history"
                  borderRadius="lg"
                />
              )}
              <Button
                leftIcon={<LuPlus />}
                variant="ghost"
                size="sm"
                onClick={startNewChat}
                borderRadius="lg"
                fontWeight="500"
              >
                New
              </Button>
            </HStack>
          </Flex>
          <DrawerCloseButton top={4} right={4} />
        </DrawerHeader>

        {/* Body */}
        <DrawerBody p={0} bg={messages.length === 0 && view === 'chat' ? 'gray.50' : 'white'}>
          {view === 'list' ? (
            // Conversation list view
            <Box p={4}>
              {isLoadingList ? (
                <Flex justify="center" py={8}>
                  <Spinner color="blue.400" />
                </Flex>
              ) : conversations.length === 0 ? (
                <Flex direction="column" align="center" py={12} color="gray.500">
                  <Box 
                    w="48px" 
                    h="48px" 
                    borderRadius="xl"
                    bg="gray.100"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={3}
                  >
                    <LuMessageCircle size={24} />
                  </Box>
                  <Text fontSize="sm" fontWeight="500" color="gray.600">
                    No conversations yet
                  </Text>
                  <Text fontSize="xs" color="gray.400" mt={1}>
                    Start exploring your market data
                  </Text>
                  <Button
                    mt={4}
                    size="sm"
                    bgGradient="linear(to-br, blue.400, blue.600)"
                    color="white"
                    leftIcon={<LuPlus />}
                    onClick={startNewChat}
                    borderRadius="lg"
                    _hover={{ bgGradient: "linear(to-br, blue.500, blue.700)" }}
                  >
                    Start a chat
                  </Button>
                </Flex>
              ) : (
                <VStack spacing={2} align="stretch">
                  {conversations.map((conv) => (
                    <Box
                      key={conv.conversation_id}
                      p={3}
                      borderRadius="xl"
                      cursor="pointer"
                      bg="white"
                      _hover={{ bg: listHoverBg, shadow: 'sm' }}
                      onClick={() => openConversation(conv.conversation_id)}
                      borderWidth="1px"
                      borderColor={borderColor}
                      transition="all 0.15s"
                    >
                      <Text fontWeight="500" fontSize="sm" noOfLines={1} color="gray.700">
                        {conv.title}
                      </Text>
                      <HStack mt={1.5} spacing={2}>
                        <Text fontSize="xs" color="gray.500">
                          {conv.message_count} messages
                        </Text>
                        <Box w="3px" h="3px" borderRadius="full" bg="gray.300" />
                        <Text fontSize="xs" color="gray.400">
                          {new Date(conv.updated_at).toLocaleDateString()}
                        </Text>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>
          ) : (
            // Chat view
            <Flex direction="column" h="full">
              <Flex flex="1" overflowY="auto" p={4} direction="column">
                <Box mt={messages.length > 0 ? "auto" : undefined}>
                {messages.length === 0 && !isLoading ? (
                  // Modern empty state with quick prompts
                  <Flex direction="column" align="center" py={6}>
                    <Box
                      w="56px"
                      h="56px"
                      borderRadius="xl"
                      bgGradient="linear(to-br, blue.400, purple.500)"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mb={4}
                      shadow="lg"
                      animation={`${float} 3s ease-in-out infinite`}
                    >
                      <Icon as={LuSparkles} color="white" boxSize={6} />
                    </Box>
                    
                    <Text fontSize="lg" fontWeight="600" color="gray.800" mb={1}>
                      How can I help?
                    </Text>
                    <Text fontSize="sm" color="gray.500" textAlign="center" mb={6} px={4}>
                      Ask about your market data, or try one of these:
                    </Text>

                    {/* Quick prompts */}
                    <VStack spacing={2} w="100%">
                      {QUICK_PROMPTS.map((prompt, idx) => (
                        <QuickPromptButton
                          key={idx}
                          icon={prompt.icon}
                          text={prompt.text}
                          gradient={prompt.gradient}
                          onClick={() => handleQuickPrompt(prompt.text)}
                        />
                      ))}
                    </VStack>

                    {/* Capability hints */}
                    <HStack spacing={3} mt={6} flexWrap="wrap" justify="center">
                      {['Reviews', 'Trends', 'Reports'].map((cap, i) => (
                        <HStack key={i} spacing={1}>
                          <Box w="4px" h="4px" borderRadius="full" bg="green.400" />
                          <Text fontSize="xs" color="gray.400">{cap}</Text>
                        </HStack>
                      ))}
                    </HStack>
                  </Flex>
                ) : (
                  <VStack spacing={4} align="stretch">
                    {messages.map((msg, idx) => {
                      const isLastAssistantMessage = msg.role === 'assistant' && 
                        idx === messages.length - 1 || 
                        (idx === messages.length - 2 && messages[messages.length - 1]?.role === 'user');
                      
                      return (
                        <Flex
                          key={idx}
                          justify={msg.role === 'user' ? 'flex-end' : 'flex-start'}
                        >
                          <Box
                            maxW="85%"
                            px={4}
                            py={3}
                            borderRadius="2xl"
                            bg={msg.role === 'user' ? undefined : 'white'}
                            bgGradient={msg.role === 'user' ? 'linear(to-br, blue.500, blue.600)' : undefined}
                            color={msg.role === 'user' ? 'white' : 'gray.800'}
                            border={msg.role === 'assistant' ? '1px solid' : undefined}
                            borderColor={msg.role === 'assistant' ? 'gray.200' : undefined}
                            borderBottomRightRadius={msg.role === 'user' ? 'lg' : '2xl'}
                            borderBottomLeftRadius={msg.role === 'assistant' ? 'lg' : '2xl'}
                            shadow="sm"
                          >
                            {msg.role === 'assistant' ? (
                              <AssistantMessage 
                                content={msg.content} 
                                toolsCalled={msg.tools_called}
                                onFollowUpClick={handleQuickPrompt}
                                isLastMessage={isLastAssistantMessage && !isLoading}
                              />
                            ) : (
                              <Text fontSize="sm" whiteSpace="pre-wrap" lineHeight="1.5">
                                {msg.content}
                              </Text>
                            )}
                          </Box>
                        </Flex>
                      );
                    })}
                    {isLoading && (
                      <Flex justify="flex-start">
                        <Box 
                          px={4} 
                          py={3} 
                          borderRadius="2xl" 
                          borderBottomLeftRadius="lg"
                          bg="white"
                          border="1px solid"
                          borderColor="gray.200"
                          shadow="sm"
                        >
                          {streamingTools.length > 0 && (
                            <HStack spacing={1.5} mb={2} flexWrap="wrap">
                              <Flex
                                w="16px"
                                h="16px"
                                borderRadius="md"
                                bg="blue.50"
                                align="center"
                                justify="center"
                                flexShrink={0}
                              >
                                <Box as={LuWrench} size="9px" color="blue.500" />
                              </Flex>
                              {streamingTools.map((tc, i) => (
                                <Tag 
                                  key={i} 
                                  size="sm" 
                                  bg="blue.50" 
                                  borderRadius="full"
                                  border="1px solid"
                                  borderColor="blue.100"
                                >
                                  <TagLabel fontSize="xs" color="blue.600" fontWeight="500">
                                    {TOOL_LABELS[tc.tool] || tc.tool}
                                  </TagLabel>
                                </Tag>
                              ))}
                            </HStack>
                          )}
                          <HStack spacing={2}>
                            <Box
                              w="20px"
                              h="20px"
                              borderRadius="md"
                              bgGradient="linear(to-br, blue.400, purple.500)"
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                            >
                              <Spinner size="xs" color="white" thickness="2px" />
                            </Box>
                            <Text fontSize="sm" color="gray.600" fontWeight="500">
                              {streamingTools.length > 0 ? 'Analyzing...' : 'Thinking...'}
                            </Text>
                          </HStack>
                        </Box>
                      </Flex>
                    )}
                    <div ref={messagesEndRef} />
                  </VStack>
                )}
                </Box>
              </Flex>
            </Flex>
          )}
        </DrawerBody>

        {/* Input footer - only in chat view */}
        {view === 'chat' && (
          <DrawerFooter 
            borderTopWidth="1px" 
            borderColor={borderColor} 
            p={3}
            bg="white"
          >
            <VStack w="full" spacing={2}>
              <HStack 
                w="full" 
                spacing={2}
                bg="gray.50"
                borderRadius="xl"
                px={3}
                py={1.5}
                border="1px solid"
                borderColor={borderColor}
                transition="all 0.2s"
                _focusWithin={{
                  borderColor: 'blue.300',
                  shadow: '0 0 0 2px rgba(66, 153, 225, 0.15)',
                }}
              >
                <Input
                  ref={inputRef}
                  placeholder="Ask anything..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  isDisabled={isLoading}
                  variant="unstyled"
                  size="md"
                  fontSize="sm"
                  _placeholder={{ color: 'gray.400' }}
                />
                <IconButton
                  icon={<LuSend />}
                  bgGradient={inputValue.trim() ? "linear(to-br, blue.400, blue.600)" : undefined}
                  bg={inputValue.trim() ? undefined : "gray.200"}
                  color={inputValue.trim() ? "white" : "gray.400"}
                  borderRadius="lg"
                  size="sm"
                  onClick={() => handleSend()}
                  isDisabled={!inputValue.trim() || isLoading}
                  aria-label="Send message"
                  _hover={{
                    bgGradient: inputValue.trim() ? "linear(to-br, blue.500, blue.700)" : undefined,
                    bg: inputValue.trim() ? undefined : "gray.300",
                  }}
                />
              </HStack>
              <Text fontSize="xs" color="gray.400" textAlign="center">
                AI analyzes your market data in real-time
              </Text>
            </VStack>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}

export default ChatDrawer;
